/**
 * Instance-level dashboard cells and the expandable component detail.
 *
 * Ported from `componentsCell` / `cvesCell` / `detailTable` of
 * app/web/dashboard.html, with two deliberate changes:
 *
 *   1. CVE ids open their public source; clicks do not toggle the row.
 *   2. The detail table shows KEV/EPSS decision support per CVE, fetched lazily
 *      (see useExploitability) and failing soft to nothing.
 *
 * The group rows, the selection checkboxes and the notify status live in
 * GroupCells.tsx.
 */

import { cvssTone } from '../../components/CvssScore';
import { useEffect, type ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import { Chip, ChipList } from '../../components/Chip';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import ManufacturerLogo from '../../components/ManufacturerLogo';
import Spinner from '../../components/Spinner';
import { DASH, countLabel, fmtDate, fmtDay, fmtScore, fmtVersion } from '../../lib/format';
import type { KomponenteBlock, OverviewRow } from '../../lib/types';
import { fmtPercent, orFallback } from './dashboardModel';
import type { ExploitabilityEntry, ExploitabilityStore } from './useExploitability';

/** How many chips a collapsed row shows before it says "+N". */
const MAX_COMPONENT_CHIPS = 3;
const MAX_CVE_CHIPS = 4;

/* ------------------------------------------------------------------------- *
 * Betroffene Komponenten
 * ------------------------------------------------------------------------- */

/**
 * Manufacturer logo, name and a boxed version — the same identity the
 * component catalog shows, so a component looks alike everywhere. Affected
 * versions keep the KEV signal on their box.
 */
function ComponentIdentity({
  komponente,
  compact = false,
}: {
  komponente: KomponenteBlock;
  compact?: boolean;
}): ReactNode {
  const hersteller = komponente.hersteller?.trim() ?? '';
  return (
    <div className={`dash-comp${compact ? ' is-compact' : ''}`}>
      <ManufacturerLogo manufacturer={hersteller} className="dash-comp-logo" />
      <div className="dash-comp-name">
        <strong>{orFallback(komponente.name, 'Unbenannte Komponente')}</strong>
        <span>{hersteller || 'Hersteller nicht angegeben'}</span>
      </div>
      <span
        className={`dash-comp-version${komponente.kev_betroffen ? ' is-affected' : ''}`}
        title={komponente.kev_betroffen ? 'Betroffene Version' : undefined}
      >
        {fmtVersion(komponente.version)}
      </span>
    </div>
  );
}

export function ComponentsCell({ row }: { row: OverviewRow }): ReactNode {
  const total = row.komponenten.length;
  if (row.affected_count === 0) {
    return (
      <>
        <Chip tone="good">keine KEV-Treffer</Chip>
        <div className="prod">{countLabel(total, 'Komponente geprüft', 'Komponenten geprüft')}</div>
      </>
    );
  }
  const affected = row.komponenten.filter((komponente) => komponente.kev_betroffen);
  const shown = affected.slice(0, MAX_COMPONENT_CHIPS);
  const rest = affected.length - shown.length;
  return (
    <>
      <div>
        <span className="count-pill">{row.affected_count}</span>{' '}
        <span className="muted">von {total} betroffen</span>
      </div>
      <div className="dash-comp-list">
        {shown.map((komponente, index) => (
          <ComponentIdentity key={komponente.id ?? `k-${index}`} komponente={komponente} compact />
        ))}
        {rest > 0 ? <span className="muted">+{rest} weitere</span> : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------- *
 * KEV-CVEs
 * ------------------------------------------------------------------------- */

export function CvesCell({
  cves,
  max = MAX_CVE_CHIPS,
}: {
  cves: readonly string[];
  max?: number;
}): ReactNode {
  if (cves.length === 0) return <span className="muted">{DASH}</span>;
  const shown = cves.slice(0, max);
  const rest = cves.length - shown.length;
  return (
    <ChipList>
      {shown.map((cve) => (
        <CveLink key={cve} cve={cve} />
      ))}
      {rest > 0 ? <span className="muted">+{rest}</span> : null}
    </ChipList>
  );
}

/* ------------------------------------------------------------------------- *
 * Detail area — component sub-table with KEV/EPSS chips and CVE links
 * ------------------------------------------------------------------------- */


/** KEV/EPSS chips for one CVE. Renders nothing while nothing is known yet. */
function ExploitabilityChips({ entry }: { entry: ExploitabilityEntry | undefined }): ReactNode {
  if (entry === undefined) return null;
  if (entry.state === 'loading') {
    return (
      <span className="chip" title="Exploitability wird geladen">
        <Spinner label="Exploitability wird geladen" /> EPSS
      </span>
    );
  }
  if (entry.state === 'unavailable') {
    return (
      <span className="chip muted" title={entry.message}>
        EPSS n. v.
      </span>
    );
  }

  const { facts } = entry;
  const chips: ReactNode[] = [];
  if (facts.known_exploited) {
    chips.push(
      <Chip
        key="kev"
        tone="alert"
        title={`${facts.kev_sources.filter(Boolean).join(', ') || 'KEV'}${facts.kev_date_added ? `, veröffentlicht am ${fmtDay(facts.kev_date_added)}` : ''}${
          facts.kev_due_date ? `, Frist ${fmtDay(facts.kev_due_date)}` : ''
        }`}
      >
        KEV
      </Chip>,
    );
  }
  if (facts.kev_known_ransomware === 'Known') {
    chips.push(
      <Chip key="ransom" tone="alert" title="Bekannter Einsatz in Ransomware-Kampagnen">
        Ransomware
      </Chip>,
    );
  }
  if (facts.epss_score !== null) {
    const hot = facts.epss_score >= facts.epss_threshold;
    chips.push(
      <Chip
        key="epss"
        tone={hot ? 'warn' : 'neutral'}
        title={`EPSS-Wahrscheinlichkeit (FIRST.org), Schwelle ${fmtPercent(facts.epss_threshold)}${
          facts.epss_percentile !== null ? ` · Perzentil ${fmtPercent(facts.epss_percentile)}` : ''
        }`}
      >
        EPSS {fmtPercent(facts.epss_score)}
      </Chip>,
    );
  }
  if (facts.cvss_base_score !== null) {
    chips.push(
      <Chip key="cvss" tone={cvssTone(facts.cvss_base_score)} title={facts.severity ?? 'CVSS Base Score'}>
        CVSS {fmtScore(facts.cvss_base_score)}
      </Chip>,
    );
  }
  // ENISA EUVD: shown when it adds something DT does not have — a CVSS 4.0
  // score (NVD is still on 3.1 for most OT CVEs) or the EU id for CRA
  // reporting. Clickable, because the next question is always "what does the EU
  // record say".
  if (entry.euvd !== null) {
    const euvd = entry.euvd;
    const score = euvd.cvss_base_score;
    const label = score !== null ? `EUVD ${fmtScore(score)}` : 'EUVD';
    chips.push(
      <a
        key="euvd"
        className="chip eu"
        href={euvd.url ?? undefined}
        target="_blank"
        rel="noreferrer"
        title={
          `${euvd.id}${euvd.cvss_version ? ` · CVSS ${euvd.cvss_version}` : ''}` +
          `${euvd.cvss_vector ? ` · ${euvd.cvss_vector}` : ''}` +
          `${euvd.assigner ? ` · CNA ${euvd.assigner}` : ''}` +
          `${euvd.aliases.length > 0 ? ` · auch ${euvd.aliases.join(', ')}` : ''}` +
          ' · ENISA EU Vulnerability Database (öffnet in neuem Tab)'
        }
      >
        {label}
      </a>,
    );
    if (euvd.known_exploited_eu) {
      chips.push(
        <Chip key="euvd-exploited" tone="alert" title={`${euvd.id}: von der EU als ausgenutzt geführt`}>
          EU-exploited
        </Chip>,
      );
    }
  }
  if (!entry.inDependencyTrack) {
    chips.push(
      <Chip key="dt" tone="neutral" title="Dependency-Track kennt diese Schwachstelle nicht">
        nicht in DT
      </Chip>,
    );
  }
  return <>{chips}</>;
}

function KevStatusCell({
  komponente,
  store,
}: {
  komponente: KomponenteBlock;
  store: ExploitabilityStore;
}): ReactNode {
  if (!komponente.kev_betroffen) return <Chip tone="good">sauber</Chip>;
  if (komponente.kev_cves.length === 0) {
    return <Chip tone="alert" title="Als betroffen markiert, aber ohne CVE-Liste">betroffen</Chip>;
  }
  return (
    <div className="dash-cve-facts">
      {komponente.kev_cves.map((cve) => (
        <div className="dash-cve-fact" key={cve}>
          <CveLink cve={cve} />
          {store.enabled ? <ExploitabilityChips entry={store.entries.get(cve.toUpperCase())} /> : null}
        </div>
      ))}
    </div>
  );
}

export interface KomponentenDetailProps {
  row: OverviewRow;
  store: ExploitabilityStore;
}

/** The expanded area of a product row: its resolved configuration. */
export function KomponentenDetail({ row, store }: KomponentenDetailProps): ReactNode {
  const { request } = store;
  // The joined ids — not the array identity — are the real dependency: every
  // refresh hands us a new array holding the same CVEs and must not refetch.
  const cveKey = row.kev_cves.join(' ');
  useEffect(() => {
    request(cveKey === '' ? [] : cveKey.split(' '));
  }, [request, cveKey]);

  const columns: DataTableColumn<KomponenteBlock>[] = [
    {
      key: 'komponente',
      header: 'Komponente / Version',
      width: '34%',
      render: (komponente) => <ComponentIdentity komponente={komponente} />,
    },
    {
      key: 'kev',
      header: 'KEV-Status',
      width: '30%',
      render: (komponente) => <KevStatusCell komponente={komponente} store={store} />,
    },
    {
      key: 'benachrichtigung',
      header: 'Benachrichtigung',
      width: '18%',
      render: (komponente) => komponente.meldungen.length > 0 ? (
        <ChipList>
          {komponente.meldungen.map((meldung, index) => (
            <Chip
              key={meldung.id ?? `meldung-${index}`}
              tone={meldung.status === 'OFFEN' ? 'warn' : 'good'}
              title={`${orFallback(meldung.titel, 'Benachrichtigung')} · erstellt ${fmtDate(
                meldung.erstellt_am,
              )}`}
            >
              {meldung.status ?? 'OFFEN'}
            </Chip>
          ))}
        </ChipList>
      ) : (
        <span className="muted">{komponente.kev_betroffen ? 'keine' : DASH}</span>
      ),
    },
    {
      key: 'scan',
      header: 'Letzter Scan',
      width: '18%',
      cellClassName: 'muted',
      render: (komponente) => fmtDate(komponente.letzter_kev_scan),
    },
  ];

  if (row.komponenten.length === 0) {
    return (
      <div className="dash-detail-note">
        Keine Komponenten: Für diese Produktinstanz ist keine Produktversion mit gültiger Stückliste
        hinterlegt.
      </div>
    );
  }

  return (
    <>
      <div className="dash-subwrap">
        <DataTable
          rows={row.komponenten}
          columns={columns}
          rowKey={(komponente) => komponente.id ?? `komponente-${row.komponenten.indexOf(komponente)}`}
          mobileBreakpoint={720}
          pageSize={10}
          paginationResetKey={String(row.produkt.id)}
          resetPageOnRowsChange={false}
          caption="Komponenten der Produktkonfiguration"
        />
      </div>

      {row.benachrichtigungen.length > 0 ? (
        <div className="dash-detail-note">
          <b>Benachrichtigungen dieser Instanz:</b>{' '}
          {row.benachrichtigungen.map((block, index) => (
            <span key={block.id ?? `ben-${index}`} className="dash-ben">
              #{block.id ?? '?'} {block.status}
              {block.empfaenger ? ` · ${block.empfaenger}` : ''}
              {block.versendet_am ? ` · versendet ${fmtDate(block.versendet_am)}` : ''}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}
